import type { PollDefinition, PollState, Post } from "@/entities/post/types";

export type ThreadTagKind = "fixed" | "free";
export type ThreadTagSource = "op" | "ai" | "moderator";

/** backend 公開APIのタグ形状（addedAtは公開レスポンスに含まれない） */
export interface ThreadTag {
  name: string;
  kind: ThreadTagKind;
  source: ThreadTagSource;
}

export type ThreadRelayState = "WAITING" | "PENDING" | "CREATED" | "STOPPED";

export interface ThreadRelay {
  seriesId: string;
  enabled: boolean;
  state: ThreadRelayState;
  previousThreadId: string | null;
  nextThreadId: string | null;
  stopReason: string | null;
  judgedUpCount: number | null;
  judgedAt: string | null;
}

/** backend ThreadSummary */
export interface ThreadSummary {
  id: string;
  opPost: Post;
  replyCount: number;
  createdAt: string;
  bumpedAt: string;
  expiresAt: string | null;
  isPermanent: boolean;
  isSage: boolean;
  forceDisplayId: boolean;
  tags: ThreadTag[];
  closedAt?: string | null;
  allowImageReplies?: boolean;
  threadNgWords?: string[];
  /** 旧APIでは省略される。スレ立て代行の公開状態。 */
  threadRelay?: ThreadRelay | null;
  /** 総合カタログでの取得元板slug。単板カタログのレスポンスには付かない。 */
  b?: string;
}

/** GET /api/threads/:threadId/chunks/:n の公開レス要素。 */
export interface ThreadChunkPost {
  id: string;
  seq: number;
  postSequenceNo: number | null;
  body: string;
  createdAt: string;
  displayId: string | null;
  /** 旧バックエンド互換のため受信時は省略を許容し、Post化時にnullへ正規化する。 */
  displayIdSource?: Post["displayIdSource"];
  attachments?: Post["attachments"];
  attachment?: Post["attachment"];
  /** ニュース投稿の公開metadata。旧APIでは省略される。 */
  news?: Post["news"];
  /** sage指定の投稿か。フィールド追加前のバックエンド互換のためoptional */
  sage?: boolean;
  /** OPのACTでチェス表示を有効にした場合だけtrue。旧APIでは省略される。 */
  chessEnabled?: boolean;
  /** スレ主表示か。フィールド追加前のバックエンド互換のためoptional */
  showThreadOwner?: boolean;
  poll?: PollDefinition | null;
}

/** 非公開レスの連番を保持するチャンク内非描画マーカー。 */
export interface ThreadUnavailablePost {
  seq: number;
  status: "unavailable";
}

export type ThreadChunkElement = ThreadChunkPost | ThreadUnavailablePost;

export function isThreadChunkPost(
  element: ThreadChunkElement,
): element is ThreadChunkPost {
  return !("status" in element) || element.status !== "unavailable";
}

export interface ThreadPostState {
  seq: number;
  status: "public" | "shadowed" | "trash" | "unavailable";
  reactions: {
    up: number;
  };
  poll?: PollState | null;
}

/** backend がスレッド内の公開レスだけから生成した要約。 */
export interface ThreadAiSummary {
  text: string;
  updatedAt: string;
  sourcePostSeqs: number[];
}

/** backendが公開投稿だけから生成した検索用タイトル。 */
export interface ThreadAiTitle {
  text: string;
  updatedAt: string;
  sourcePostSeqs: number[];
}

/** GET /api/threads/:threadId/state の可変状態。 */
export interface ThreadState {
  /** 旧システムの数値ID。移行threadだけが持つ。 */
  legacyThreadId: number | null;
  replyCount: number;
  bumpedAt: string;
  tags: ThreadTag[];
  closedAt: string | null;
  allowImageReplies: boolean;
  threadNgWords?: string[];
  /** 旧APIでは省略される。スレ立て代行の公開状態。 */
  threadRelay?: ThreadRelay | null;
  /** 閲覧者がスレ立て人としてレスを投稿できる場合はtrue。 */
  canShowThreadOwner: boolean;
  archivedAt?: string | null;
  expiresAt: string | null;
  isPermanent: boolean;
  isSage: boolean;
  forceDisplayId: boolean;
  /** 本人にだけ返る、詳細分析による削除通知の不透明ID。旧APIでは省略される。 */
  censorshipNotices?: Array<{ id: string }>;
  /** 旧APIでは省略される。生成前・対象外の場合はnull。 */
  aiSummary?: ThreadAiSummary | null;
  /** 旧APIでは省略される。生成前・対象外の場合はnull。 */
  aiTitle?: ThreadAiTitle | null;
  postStates: ThreadPostState[];
  newPosts?: ThreadChunkElement[];
}

/** backend ThreadView */
export interface ThreadView {
  id: string;
  /** 旧システムの数値ID。MLB等の一次仕様ゲートにのみ使用する。 */
  legacyThreadId?: number | null;
  replyCount: number;
  createdAt: string;
  bumpedAt: string;
  expiresAt: string | null;
  isPermanent: boolean;
  isSage: boolean;
  forceDisplayId: boolean;
  tags: ThreadTag[];
  posts: Post[];
  /** archived full responseに含まれる、postsへ合成する最終可変状態。 */
  postStates?: ThreadPostState[];
  closedAt: string | null;
  allowImageReplies: boolean;
  threadNgWords?: string[];
  /** 旧APIでは省略される。スレ立て代行の公開状態。 */
  threadRelay?: ThreadRelay | null;
  /** 閲覧者がスレ立て人としてレスを投稿できる場合はtrue。 */
  canShowThreadOwner: boolean;
  /** アーカイブ済みの場合のみ設定される。 */
  archivedAt?: string | null;
  /** 旧APIでは省略される。生成前・対象外の場合はnull。 */
  aiSummary?: ThreadAiSummary | null;
  /** 旧APIでは省略される。生成前・対象外の場合はnull。 */
  aiTitle?: ThreadAiTitle | null;
}

export interface TopPage {
  announcements: string[];
  threads: ThreadSummary[];
}

export interface Catalog {
  sort: "bump" | "new" | "old" | "replies" | "momentum" | "soudane";
  threads: ThreadSummary[];
  pagination: {
    page: number;
    limit: number;
    hasNextPage: boolean;
  };
}

export interface SearchResponse {
  query: string;
  posts: Post[];
}

/** 既存UIが Thread と呼んでいるドメイン型 */
export type Thread = ThreadSummary;
export type ThreadsResponse = Catalog;
export type ThreadDetailResponse = ThreadView;
